import { useState, useEffect } from "react";
import {
  Search,
  Filter,
  Eye,
  UserCheck,
  Calendar,
  MessageSquare,
  Star,
  FileText,
  Phone,
  Mail,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  Edit,
  Trash2,
  Download,
  Plus,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { recruitmentAPI } from "@/lib/api";
import CandidatureForm from "./CandidatureForm";

const ApplicationsList = ({ onBack }) => {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobFilter, setJobFilter] = useState("all");
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10,
  });

  // Modal states
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showCandidatureForm, setShowCandidatureForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [evaluationData, setEvaluationData] = useState({
    statut: "",
    note_evaluation: "",
    commentaires: "",
  });

  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    fetchApplications();
    fetchJobs();
  }, [searchTerm, statusFilter, jobFilter, pagination.current_page]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        limit: pagination.items_per_page,
        search: searchTerm || undefined,
        statut: statusFilter !== "all" ? statusFilter : undefined,
        offre_id: jobFilter !== "all" ? jobFilter : undefined,
      };

      const response = await recruitmentAPI.getApplications(params);
      setApplications(response.data.data.applications);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les candidatures",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await recruitmentAPI.getJobs({ limit: 100 });
      console.log("Fetched jobs:", response.data.data.jobs);
      setJobs(response.data.data.jobs);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setJobs([]); // Set to empty array on error
    }
  };

  const handleEvaluate = async () => {
    try {
      await recruitmentAPI.evaluateApplication(
        selectedApplication.id,
        evaluationData
      );
      toast({
        title: "Succès",
        description: "Candidature évaluée avec succès",
      });
      setShowEvaluationModal(false);
      setSelectedApplication(null);
      setEvaluationData({ statut: "", note_evaluation: "", commentaires: "" });
      fetchApplications();
    } catch (error) {
      console.error("Error evaluating application:", error);
      toast({
        title: "Erreur",
        description: "Erreur lors de l'évaluation de la candidature",
        variant: "destructive",
      });
    }
  };

  const openEvaluationModal = (application) => {
    setSelectedApplication(application);
    setEvaluationData({
      statut: application.statut,
      note_evaluation: application.note_evaluation?.toString() || "",
      commentaires: application.commentaires || "",
    });
    setShowEvaluationModal(true);
  };

  const openEditModal = (application) => {
    setSelectedApplication(application);
    setShowCandidatureForm(true);
  };

  const openDeleteDialog = (application) => {
    setSelectedApplication(application);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    try {
      await recruitmentAPI.deleteApplication(selectedApplication.id);
      toast({
        title: "Succès",
        description: "Candidature supprimée avec succès",
      });
      setShowDeleteDialog(false);
      setSelectedApplication(null);
      fetchApplications();
    } catch (error) {
      console.error("Error deleting application:", error);
      toast({
        title: "Erreur",
        description:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Erreur lors de la suppression",
        variant: "destructive",
      });
    }
  };

  const downloadFile = async (application, fileType) => {
    try {
      const response = await recruitmentAPI.downloadApplicationFile(
        application.id,
        fileType
      );

      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const fileName =
        fileType === "cv"
          ? application.cv_file_name ||
            `CV_${application.prenom}_${application.nom}.pdf`
          : application.motivation_file_name ||
            `Lettre_Motivation_${application.prenom}_${application.nom}.pdf`;

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      recue: "bg-blue-100 text-blue-800",
      preselectionne: "bg-yellow-100 text-yellow-800",
      entretien: "bg-purple-100 text-purple-800",
      acceptee: "bg-green-100 text-green-800",
      refusee: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status) => {
    const labels = {
      recue: "Reçue",
      preselectionne: "Présélectionné",
      entretien: "Entretien",
      acceptee: "Acceptée",
      refusee: "Refusée",
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      recue: <Clock className="h-4 w-4" />,
      preselectionne: <Star className="h-4 w-4" />,
      entretien: <MessageSquare className="h-4 w-4" />,
      acceptee: <CheckCircle className="h-4 w-4" />,
      refusee: <XCircle className="h-4 w-4" />,
    };
    return icons[status] || <FileText className="h-4 w-4" />;
  };

  const canEvaluate = user?.role === "admin" || user?.role === "rh";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour au tableau de bord
            </Button>
          )}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Candidatures</h1>
            <p className="text-gray-600">
              Gérez et évaluez les candidatures reçues
            </p>
          </div>
        </div>
        {canEvaluate && (
          <Button
            onClick={() => {
              setSelectedApplication(null);
              setShowCandidatureForm(true);
            }}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Nouvelle candidature
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="recue">Reçue</SelectItem>
                <SelectItem value="preselectionne">Présélectionné</SelectItem>
                <SelectItem value="entretien">Entretien</SelectItem>
                <SelectItem value="acceptee">Acceptée</SelectItem>
                <SelectItem value="refusee">Refusée</SelectItem>
              </SelectContent>
            </Select>

            <Select value={jobFilter} onValueChange={setJobFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Offre d'emploi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les offres</SelectItem>
                {Array.isArray(jobs) &&
                  jobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.titre}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setJobFilter("all");
              }}
            >
              <Filter className="h-4 w-4 mr-2" />
              Réinitialiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Applications List */}
      <div className="grid gap-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : applications.length > 0 ? (
          applications.map((application) => (
            <Card
              key={application.id}
              className="hover:shadow-md transition-shadow"
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <UserCheck className="h-6 w-6 text-blue-600" />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {application.prenom} {application.nom}
                        </h3>
                        <Badge className={getStatusColor(application.statut)}>
                          {getStatusIcon(application.statut)}
                          <span className="ml-1">
                            {getStatusLabel(application.statut)}
                          </span>
                        </Badge>
                        {application.note_evaluation && (
                          <Badge variant="outline">
                            <Star className="h-3 w-3 mr-1" />
                            {application.note_evaluation}/20
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-6 text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-1">
                          <Mail className="h-4 w-4" />
                          <span>{application.email}</span>
                        </div>

                        {application.telephone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-4 w-4" />
                            <span>{application.telephone}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {new Date(
                              application.date_candidature
                            ).toLocaleDateString("fr-FR")}
                          </span>
                        </div>
                      </div>

                      {application.offre && (
                        <div className="mb-3">
                          <span className="text-sm font-medium text-gray-700">
                            Poste: {application.offre.titre}
                          </span>
                          {application.offre.departement && (
                            <span className="text-sm text-gray-500 ml-2">
                              • {application.offre.departement.nom}
                            </span>
                          )}
                        </div>
                      )}

                      {application.lettre_motivation && (
                        <div className="mb-3">
                          <p className="text-sm text-gray-700 line-clamp-2">
                            <span className="font-medium">Motivation: </span>
                            {application.lettre_motivation}
                          </p>
                        </div>
                      )}

                      {application.commentaires && (
                        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Commentaires d'évaluation:
                          </p>
                          <p className="text-sm text-gray-600">
                            {application.commentaires}
                          </p>
                          {application.evaluateur && (
                            <p className="text-xs text-gray-500 mt-1">
                              Évalué par {application.evaluateur.prenom}{" "}
                              {application.evaluateur.nom}
                            </p>
                          )}
                        </div>
                      )}

                      {application.entretiens &&
                        application.entretiens.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">
                              Entretiens ({application.entretiens.length}):
                            </p>
                            <div className="space-y-1">
                              {application.entretiens.map((interview) => (
                                <div
                                  key={interview.id}
                                  className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded"
                                >
                                  <span>
                                    {interview.type_entretien} -{" "}
                                    {new Date(
                                      interview.date_entretien
                                    ).toLocaleDateString("fr-FR")}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {getStatusLabel(interview.statut)}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {canEvaluate && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            onClick={() => openEditModal(application)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() => openEvaluationModal(application)}
                          >
                            <Star className="h-4 w-4 mr-2" />
                            Évaluer
                          </DropdownMenuItem>

                          {/* File download options */}
                          {(application.has_cv_file ||
                            application.has_motivation_file) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel>Télécharger</DropdownMenuLabel>
                              {application.has_cv_file && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    downloadFile(application, "cv")
                                  }
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  CV
                                </DropdownMenuItem>
                              )}
                              {application.has_motivation_file && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    downloadFile(application, "motivation")
                                  }
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Lettre de motivation
                                </DropdownMenuItem>
                              )}
                            </>
                          )}

                          {application.statut === "preselectionne" && (
                            <DropdownMenuItem
                              onClick={() => {
                                // TODO: Open interview scheduling modal
                                toast({
                                  title: "Fonctionnalité à venir",
                                  description:
                                    "La programmation d'entretiens sera bientôt disponible",
                                });
                              }}
                            >
                              <Calendar className="h-4 w-4 mr-2" />
                              Programmer entretien
                            </DropdownMenuItem>
                          )}

                          <DropdownMenuSeparator />

                          {application.statut !== "acceptee" && (
                            <DropdownMenuItem
                              onClick={() =>
                                openEvaluationModal({
                                  ...application,
                                  statut: "acceptee",
                                })
                              }
                              className="text-green-600"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Accepter
                            </DropdownMenuItem>
                          )}

                          {application.statut !== "refusee" && (
                            <DropdownMenuItem
                              onClick={() =>
                                openEvaluationModal({
                                  ...application,
                                  statut: "refusee",
                                })
                              }
                              className="text-red-600"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Refuser
                            </DropdownMenuItem>
                          )}

                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            onClick={() => openDeleteDialog(application)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Aucune candidature trouvée</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Page {pagination.current_page} sur {pagination.total_pages}(
            {pagination.total_items} résultat(s))
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === 1}
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  current_page: prev.current_page - 1,
                }))
              }
            >
              Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  current_page: prev.current_page + 1,
                }))
              }
            >
              Suivant
            </Button>
          </div>
        </div>
      )}

      {/* Evaluation Modal */}
      {showEvaluationModal && selectedApplication && (
        <Dialog
          open={showEvaluationModal}
          onOpenChange={setShowEvaluationModal}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Évaluer la candidature</DialogTitle>
              <DialogDescription>
                Évaluez la candidature de {selectedApplication.prenom}{" "}
                {selectedApplication.nom}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="statut">Statut</Label>
                <Select
                  value={evaluationData.statut}
                  onValueChange={(value) =>
                    setEvaluationData((prev) => ({ ...prev, statut: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recue">Reçue</SelectItem>
                    <SelectItem value="preselectionne">
                      Présélectionné
                    </SelectItem>
                    <SelectItem value="entretien">Entretien</SelectItem>
                    <SelectItem value="acceptee">Acceptée</SelectItem>
                    <SelectItem value="refusee">Refusée</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="note">Note d'évaluation (0-20)</Label>
                <Input
                  id="note"
                  type="number"
                  min="0"
                  max="20"
                  value={evaluationData.note_evaluation}
                  onChange={(e) =>
                    setEvaluationData((prev) => ({
                      ...prev,
                      note_evaluation: e.target.value,
                    }))
                  }
                  placeholder="Ex: 15"
                />
              </div>

              <div>
                <Label htmlFor="commentaires">Commentaires</Label>
                <Textarea
                  id="commentaires"
                  value={evaluationData.commentaires}
                  onChange={(e) =>
                    setEvaluationData((prev) => ({
                      ...prev,
                      commentaires: e.target.value,
                    }))
                  }
                  placeholder="Ajoutez vos commentaires sur cette candidature..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowEvaluationModal(false);
                  setSelectedApplication(null);
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleEvaluate}
                disabled={!evaluationData.statut}
              >
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Candidature Form Modal */}
      <CandidatureForm
        isOpen={showCandidatureForm}
        onClose={() => {
          setShowCandidatureForm(false);
          setSelectedApplication(null);
        }}
        candidature={selectedApplication}
        jobOffers={jobs}
        onSuccess={() => {
          fetchApplications();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer la candidature de{" "}
              {selectedApplication?.prenom} {selectedApplication?.nom} ?
              <br />
              <br />
              <strong>Cette action est irréversible.</strong> Tous les
              entretiens associés seront également supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ApplicationsList;
